import type {
  DepictSort,
  FilterData,
  FormattedDepictSort,
} from "@/lib/depict/types";
import { isEmpty } from "@frend-digital/utils";

type NextSearchParams = Record<string, string | string[] | undefined>;

export const filterKeys = {
  size_name: "Size",
  sh_swatch_desc: "Color",
  "pr_feature.value": "Feature",
  product_scollection_category_hierarchy: "Category",
  pr_features: "Features",
  /* in_stock: "In stock", */
};

export const availableSortOrders = {
  _relevance_desc: {
    field: "_relevance",
    order: "desc",
    key: "_relevance_desc",
  },
  sale_price_asc: {
    field: "sale_price",
    order: "asc",
    key: "sale_price_asc",
  },
  sale_price_desc: {
    field: "sale_price",
    order: "desc",
    key: "sale_price_desc",
  },
  product_timestamp_desc: {
    field: "product_timestamp",
    order: "desc",
    key: "product_timestamp_desc",
  },
};

type SortKey = keyof typeof availableSortOrders;

export function getSortOrders<T extends SortKey[]>(keys: T) {
  return keys.map((key) => availableSortOrders[key as SortKey]);
}

export function getSortOrderFromParam(key?: string | string[] | undefined) {
  if (typeof key !== "string") return;
  const sortValue = key.split(":");
  return {
    field: sortValue[0],
    order: sortValue[1],
  } as DepictSort;
}

export const SORT_QUERY_KEY = "sort";

export function getFiltersFromParams(
  searchParams: NextSearchParams,
  filterKeys: string[] | undefined,
) {
  if (!filterKeys) return [];
  const filters: { [key: string]: FilterData } = {};

  for (const key of filterKeys) {
    const paramValue = searchParams[key];
    if (!paramValue) continue;

    const values = Array.isArray(paramValue) ? paramValue : [paramValue];

    let expandedValues: string[] | string[][];

    if (key === "category") {
      expandedValues = values.map((value) =>
        decodeURIComponent(value).split(","),
      );
    } else {
      expandedValues = values.flatMap((value) =>
        decodeURIComponent(value).split(","),
      );
    }

    if (filters[key]) {
      if (
        key === "category" &&
        Array.isArray(filters[key].data) &&
        Array.isArray(filters[key].data[0])
      ) {
        filters[key].data = [
          ...(filters[key].data as string[][]),
          ...(expandedValues as string[][]),
        ];
      } else if (Array.isArray(filters[key].data)) {
        filters[key].data = [
          ...(filters[key].data as string[]),
          ...(expandedValues as string[]),
        ];
      }
    } else {
      filters[key] = {
        data: expandedValues,
        field: key,
        op: "in",
      };
    }
  }

  return Object.values(filters);
}

export interface ServerFilterOptions {
  filterKeys?: string[];
}

export type ServerFilterReturn = {
  filterValues: FilterData[];
  sort?: FormattedDepictSort | undefined;
  cache: boolean;
};

export function createFilters(
  searchParams: NextSearchParams,
  { filterKeys }: ServerFilterOptions,
) {
  const filterValues = getFiltersFromParams(searchParams, filterKeys);
  const sortOrder = getSortOrderFromParam(searchParams?.sort);

  const noFilters = isEmpty(filterValues);
  /*  const onlyInStock =
    filterValues.length === 1 && filterValues[0].field === "in_stock"; */

  return {
    sort: sortOrder ? sortOrder : undefined,
    filterValues,
    cache: noFilters /* || onlyInStock */ && sortOrder === undefined,
  } as ServerFilterReturn;
}
